import { Button } from '@ads/components-react';
import { ArrowPathIcon, TrashIcon } from '@heroicons/react/24/outline';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { FileUpload, ProgressBar, Skeleton, Spinner } from '@/shared/ui/atoms';

import { useObjectUrl } from '../../../hooks/useObjectUrl';
import {
  BANNER_IMAGE,
  BANNER_IMAGE_LABEL_VALUES,
  type BannerFileRejection,
  describeImageDetails,
  validateBannerImage,
} from '../../../utils/bannerMedia';
import { BannerFieldLabel } from '../BannerFieldLabel';

export interface BannerImageFieldProps {
  readonly label: string;
  /** Explicação do campo, exibida no tooltip do rótulo. */
  readonly info?: string | undefined;
  /** Título do banner, usado como nome da arte já cadastrada. */
  readonly bannerTitle: string;
  /** Arte nova escolhida (ainda não enviada). */
  readonly file: File | null;
  /** Arte já cadastrada no servidor. */
  readonly existingUrl: string | undefined;
  readonly onChange: (file: File | null) => void;
  /** Pendência vinda da validação do formulário. */
  readonly error?: string | undefined;
  /** `true` durante o envio: o card mostra o arquivo subindo. */
  readonly isUploading?: boolean;
  /** Bytes enviados (0–100); `null` quando o total é desconhecido. */
  readonly uploadProgress?: number | null;
}

/**
 * Campo de arte do banner: dropzone quando vazio, card com miniatura, metadados
 * reais e ações quando há arquivo.
 *
 * O cadastro **não apaga mídia**, só substitui — então "Substituir" vale sempre e
 * "Descartar escolha" aparece apenas sobre um arquivo recém-escolhido, que ainda
 * não subiu (descartar volta para a arte que está no cadastro). Enquanto o
 * formulário envia, o card exibe o arquivo em upload.
 *
 * Serve os dois pares de imagem do contrato (mobile e legado), com rótulo, tooltip
 * e destino diferentes em cada um.
 */
export function BannerImageField({
  label,
  info,
  bannerTitle,
  file,
  existingUrl,
  onChange,
  error,
  isUploading = false,
  uploadProgress = null,
}: BannerImageFieldProps) {
  const { t } = useTranslation('banner');
  const [rejection, setRejection] = useState<BannerFileRejection | null>(null);
  // Resolução real da arte: só o browser sabe, depois que a imagem carrega.
  const [naturalSize, setNaturalSize] = useState<{ width: number; height: number } | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const pickedUrl = useObjectUrl(file);
  const thumbnailUrl = pickedUrl ?? existingUrl;
  // Qual URL já carregou: trocar de arquivo volta ao skeleton sozinho, sem efeito de
  // reset (o `<img>` continua montado, só a `src` muda).
  const [loadedThumbnailUrl, setLoadedThumbnailUrl] = useState<string | undefined>(undefined);
  const isThumbnailLoaded = thumbnailUrl !== undefined && loadedThumbnailUrl === thumbnailUrl;
  const details = describeImageDetails({
    width: naturalSize?.width,
    height: naturalSize?.height,
    file,
    url: existingUrl,
  });

  const handlePicked = (files: File[]) => {
    const picked = files[0];
    if (!picked) return;
    const rejected = validateBannerImage(picked);
    setRejection(rejected);
    if (rejected === null) onChange(picked);
  };

  return (
    <div className="flex w-full flex-col gap-nano">
      <BannerFieldLabel label={label} {...(info !== undefined ? { info } : {})} />

      {file || existingUrl ? (
        <div className="flex w-full flex-wrap items-center gap-mini rounded-md border-hairline border-ctx-layout-border bg-ctx-layout-body p-micro">
          {/* Miniatura na proporção do container do app (205–358 × 360), para o
              operador julgar o recorte já aqui, sem abrir o preview. */}
          <div className="relative shrink-0">
            <img
              src={thumbnailUrl}
              alt={label}
              className="h-[7.5rem] w-[7.4375rem] rounded-sm border-hairline border-ctx-layout-border bg-base-gray-800 object-cover"
              onLoad={event => {
                setLoadedThumbnailUrl(thumbnailUrl);
                setNaturalSize({
                  width: event.currentTarget.naturalWidth,
                  height: event.currentTarget.naturalHeight,
                });
              }}
            />

            {isThumbnailLoaded ? null : (
              <Skeleton className="absolute inset-none h-full w-full animate-none" />
            )}

            {isUploading ? (
              <span className="absolute inset-none flex items-center justify-center rounded-sm bg-base-gray-900 opacity-medium">
                {uploadProgress === null ? <Spinner size="sm" /> : null}
              </span>
            ) : null}
          </div>

          <div className="flex min-w-[12rem] flex-1 flex-col gap-nano">
            <div className="flex flex-col gap-quark">
              <span className="ts-subtitle-xxs text-ctx-content-title">
                {file ? file.name : bannerTitle.trim() || t('banner.form.media.imageCurrent')}
              </span>
              {/* Durante o envio o que importa é o progresso; fora dele, os dados
                  reais do arquivo (resolução, tamanho, formato). */}
              {isUploading ? (
                <span className="flex items-center gap-nano ts-caption-xxs text-ctx-content-base">
                  <span className="shrink-0">{t('banner.form.media.uploading')}</span>
                  {uploadProgress === null ? (
                    <Spinner size="sm" />
                  ) : (
                    <span className="min-w-[5rem] flex-1">
                      <ProgressBar shouldShowPercentage progressPercentage={uploadProgress} />
                    </span>
                  )}
                </span>
              ) : (
                <span className="ts-caption-xxs text-ctx-content-base">{details}</span>
              )}
            </div>

            <div className="flex flex-wrap items-center gap-nano">
              <Button
                type="button"
                hierarchy="secondary"
                size="sm"
                leadingIcon={ArrowPathIcon}
                disabled={isUploading}
                onClick={() => inputRef.current?.click()}
              >
                {t('banner.form.media.replace')}
              </Button>
              {file ? (
                <Button
                  type="button"
                  hierarchy="tertiary"
                  size="sm"
                  leadingIcon={TrashIcon}
                  disabled={isUploading}
                  onClick={() => {
                    onChange(null);
                    setRejection(null);
                  }}
                >
                  {t('banner.form.media.discard')}
                </Button>
              ) : null}
            </div>
          </div>

          {/* Um input só, acionado pelo botão via ref: rótulo envolvendo input
              dentro do form disparava dois cliques no seletor de arquivo. */}
          <input
            ref={inputRef}
            type="file"
            accept={BANNER_IMAGE.ACCEPT}
            className="sr-only"
            disabled={isUploading}
            onChange={event => {
              const files = event.target.files ? Array.from(event.target.files) : [];
              event.target.value = '';
              handlePicked(files);
            }}
          />
        </div>
      ) : (
        <FileUpload
          files={[]}
          addFiles={handlePicked}
          removeFile={() => onChange(null)}
          accept={BANNER_IMAGE.ACCEPT}
          hasError={rejection !== null || error !== undefined}
          disabled={isUploading}
        />
      )}

      {rejection ? (
        <p className="ts-caption-xxs text-base-danger-700">
          {t(
            rejection === 'type'
              ? 'banner.form.media.errors.imageType'
              : 'banner.form.media.errors.imageSize',
            BANNER_IMAGE_LABEL_VALUES,
          )}
        </p>
      ) : error !== undefined ? (
        <p className="ts-caption-xxs text-base-danger-700">{error}</p>
      ) : null}
    </div>
  );
}
